import React from 'react';
import { Button, Flex, Select } from 'antd';
import { CopyFilled, ReloadOutlined } from '@ant-design/icons';
import { OptionalTooltipWrapper } from '../../../../componentsV2/OptionalTooltipWrapper';
import { SubscribeToProPlanLink } from '../../../../componentsV2/UpgradeToPro';
import { useQuery } from '@tanstack/react-query';
import { coreService } from '../../../../services/core/core-service';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../../redux/selectors';
import { Framework, FrameworkExecution } from '../../type';
import { ReactElement, useState } from 'react';
import { useToast } from '../../../../hooks/useToast';
import useFeatures from '../../../../hooks/useFeatures';

import Styles from './styles';
import { Modal } from '../../../../componentsV2/Modal';
import { MeetingTypeList } from '../../../home/tabs/UpcomingsTab/components/MeetingTypeSelector';
import { useSearchParams } from 'react-router-dom';
import { useAnalytics } from '../../../analytics/hooks/useAnalytics';
import { DateSelector } from '../../../shared/components/DateSelector';
import { AxiosError } from 'axios';

const { Container, NoFrameworks } = Styles;

type OnGenerateProps = {
  from?: string;
  to?: string;
  meetingType?: string[];
};

interface Props {
  setLastExecution: (le: FrameworkExecution | null) => void;
  fetchLastExecution: (id: string) => Promise<FrameworkExecution | null>;
  onGenerate: (id: string, context?: OnGenerateProps) => Promise<FrameworkExecution | null>;
  onCopy: (name: string, updatedAt: string) => void;
  showContext: boolean;
  children: ReactElement;
  disabled: boolean;
  key: string;
}

type FrameWorkExecutionProps = {
  selectedFramework: string;
  onClose: () => void;
  open: boolean;
  onGenerate: (context: OnGenerateProps) => unknown;
};

const FrameworkExecutionOptions: React.FC<FrameWorkExecutionProps> = ({
  onClose,
  open,
  onGenerate,
  selectedFramework,
}) => {
  const { sendEvent } = useAnalytics();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedMeetingTypes, setSelectedMeetingTypes] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[null, null] | [Date, Date]>([null, null]);
  const [dateOption, setDateOption] = useState('30d');

  const resetFramework = () => {
    setSelectedMeetingTypes([]);
    setDateRange([null, null]);
    setDateOption('30d');
  };

  const cancelFramework = () => {
    resetFramework();
    onClose();
    setIsLoading(false);
  };

  const isGenerateEnabled = dateRange[0] !== null && dateRange[0] !== null;

  return (
    <Modal
      title="Create Insight Framework Report"
      onClose={cancelFramework}
      open={open}
      width={550}
      primaryAction={{
        label: 'Generate',
        loading: isLoading,
        disabled: !isGenerateEnabled,
        onClick: async () => {
          setIsLoading(true);
          await onGenerate({
            from: dateRange[0]?.toISOString(),
            to: dateRange[1]?.toISOString(),
            meetingType: selectedMeetingTypes,
          });
          setIsLoading(false);
          sendEvent('account framework generated');
        },
      }}
      secondaryAction={{
        label: 'Cancel',
        onClick: cancelFramework,
      }}
    >
      <Flex vertical gap={12}>
        <Flex vertical gap={4}>
          <span style={{ fontFamily: 'Cera Pro Medium', fontWeight: 500, color: '#282828' }}>
            Framework selected
          </span>
          <span>{selectedFramework}</span>
        </Flex>
        <Flex vertical gap={4}>
          <span style={{ fontFamily: 'Cera Pro Medium', fontWeight: 500, color: '#282828' }}>
            Date range
          </span>
          <Flex>
            <DateSelector
              defaultValues={[null, null]}
              value={dateOption}
              direction="past"
              predefinedRanges={{
                today: true,
                '1d': false,
                '7d': true,
                '12m': false,
                '30d': true,
                '60d': true,
                '90d': true,
                '120d': true,
                '3m': false,
                '6m': false,
              }}
              onDateOptionChanged={setDateOption}
              onDateRangeChanged={setDateRange}
            />
          </Flex>
        </Flex>
        <Flex vertical gap={4}>
          <span style={{ fontFamily: 'Cera Pro Medium', fontWeight: 500, color: '#282828' }}>
            Meeting types (optional)
          </span>
          <Select
            value={selectedMeetingTypes}
            onChange={value => {
              setSelectedMeetingTypes(
                value.includes('select-all')
                  ? MeetingTypeList
                  : value.filter(option => option !== 'select-all'),
              );
            }}
            options={[
              { value: 'select-all', label: 'Select All' },
              ...MeetingTypeList.map(v => ({ label: v, value: v })),
            ]}
            mode="tags"
          />
        </Flex>
      </Flex>
    </Modal>
  );
};

export const FrameworksHeader: React.FC<Props> = ({
  setLastExecution,
  fetchLastExecution,
  onGenerate,
  onCopy,
  showContext,
  children,
  disabled,
  key,
}) => {
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Framework | null | undefined>(undefined);
  const [refetch, setRefetch] = useState<NodeJS.Timeout | null>(null);
  const [displayContext, setDisplayContext] = useState(false);

  const [queryParams] = useSearchParams();

  const { error } = useToast();

  const user = useSelector(selectUser);
  const feature = useFeatures();
  const availableFeature = feature.meddicc;

  const handleSelect = async (f: Framework | null) => {
    if (!f?.id) return;

    if (f.id !== selected?.id && refetch) {
      clearTimeout(refetch);
      setRefetch(null);
    }

    setSelected(f);
    try {
      setLoading(true);
      const fe = await fetchLastExecution(f?.id);
      setLastExecution(fe);

      if (fe?.isProcessing) {
        setRefetch(
          setTimeout(() => {
            handleSelect(f);
          }, 5000),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const generateFramework = async (context?: OnGenerateProps) => {
    if (!selected) return;

    try {
      setLoading(true);
      await onGenerate(selected.id, context);
      setDisplayContext(false);
      setLastExecution({ isProcessing: true } as FrameworkExecution);
      setRefetch(
        setTimeout(() => {
          handleSelect({ ...selected });
        }, 2000),
      );
    } catch (e: unknown) {
      const message = (e as AxiosError).response?.data.message;
      console.log(message);
      setLoading(false);
      error(message);
    }
  };

  const handleGenerate = async () => {
    if (!selected) return;

    if (showContext) {
      setDisplayContext(true);
      return;
    }

    await generateFramework();
  };

  const { data, isFetching } = useQuery({
    queryKey: ['frameworks', key],
    queryFn: async () => {
      if (!user?.membership.organization.id) return [];
      const d = await coreService.fetchFrameworksByWorkspaceId(user?.membership.organization.id);
      if (d.length > 0) {
        const framework = d.find(({ id }) => id === queryParams.get('framework')) ?? d[0];
        handleSelect(framework);
        return d;
      } else {
        setLoading(false);
        setLastExecution(null);
        return [];
      }
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const options = data?.map(f => ({ label: f.name, value: f.id })) || [];
  const val = options.find(f => f.value === selected?.id);
  const noData = data?.length === 0 && !loading;

  document.addEventListener('stop-framework-polling', () => {
    if (refetch) clearTimeout(refetch);
  });

  const handleGenerateOpts = async (context: OnGenerateProps) => {
    await generateFramework(context);
  };

  return (
    <>
      <Container>
        <FrameworkExecutionOptions
          selectedFramework={selected?.name || ''}
          onGenerate={handleGenerateOpts}
          open={displayContext}
          onClose={() => setDisplayContext(false)}
        />
        <Select
          placeholder="Select a framework"
          options={options}
          disabled={isFetching || loading}
          value={val}
          style={{ width: 350 }}
          onSelect={(v, i) => handleSelect(data?.find(f => f.id === i.value) || null)}
        />

        <OptionalTooltipWrapper display={!availableFeature} value={<SubscribeToProPlanLink />}>
          <Button
            type="default"
            icon={<ReloadOutlined />}
            disabled={isFetching || loading || disabled || noData}
            onClick={() => handleGenerate()}
          >
            Regenerate
          </Button>
        </OptionalTooltipWrapper>

        <Button
          icon={<CopyFilled />}
          onClick={() => onCopy(selected?.name || '', selected?.createdAt || '')}
          style={{ borderRadius: '8px' }}
          disabled={isFetching || loading || disabled || noData}
        >
          Copy All
        </Button>
      </Container>

      {noData ? (
        <NoFrameworks
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}
        >
          <span style={{ width: '500px', textAlign: 'center' }}>
            Your workspace hasn't created any reporting frameworks yet. Go to your{' '}
            <a href="/settings?tab=framework"> frameworks settings</a> to create your own.
          </span>
        </NoFrameworks>
      ) : (
        React.cloneElement(children, {
          ...children.props,
          loading: isFetching || loading || children.props.loading,
          selectedFrameworkName: selected?.name,
          onGenerate: handleGenerate,
        })
      )}
    </>
  );
};
